import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { fetchOutageInfo } from "../../../../shared/hooks/useFetchAccountInfo";
import API_URLS from "../../Utils/axr";
import store from "../store";
import { EC_DW_LOGGING_STEPS } from "../../../../shared/utils/Constants";
import DwLogutil from "../../Utils/DwLogutil";
import { addHours, format, getMinutes, isValid } from "date-fns";
import { v4 as uuidv4 } from "uuid";
import { outageBannerGTM } from "../../Utils/googleTags";

type outageInformationInitialStateType = {
  outageInformation: any;
  closeOutage: boolean;
  noOutage: boolean;
  openOutage: boolean;
  hideclose:boolean;
  outageDate: any;
  outageTime:any;
  loading: boolean;
  error: any;
};
const initialState: outageInformationInitialStateType = {
  outageInformation: null,
  loading: false,
  error: null,
  closeOutage: false,
  noOutage: false,
  openOutage: false,
  hideclose: false,
  outageDate: null,
  outageTime: null
};

export const getOutageInformation = createAsyncThunk(
  "/outageInformation",
  async (billingAccountNumber: string) => {
    try {
      const reduxStore = store.getState();
      const headers = reduxStore?.brightspeedToken?.headers;
      const user = reduxStore?.user?.data;
      const companyName = reduxStore?.user?.companyName;
      let url=API_URLS['outageInformationUrl_URL']
      const response = await fetchOutageInfo(
        {
         "": url
        },
        {
          "ban": billingAccountNumber, 
          "interactionShortDescription": "outage-information",
          "closedTicketLookback": "1d",
          "closedOutageLookback":"1d",
          "forceNewInteraction": false,
          "transactionId":  uuidv4(), 
          "closedOutageLookbackFieldname": "incident_resolved_time",
          "sendingSystem": "DIGITAL-ACM",
          "userId": user?.userName 
        },
        headers
      );
      if(response?.data) {
        if(response.data?.open?.length > 0){
          DwLogutil({stepName:EC_DW_LOGGING_STEPS?.open_outage_banner});
          outageBannerGTM("outage_banner",companyName);
          response.data.openOutage = true;
          response.data.hideclose = true;
          const dueTicketDate = findHighestDueDate(response?.data?.open);
          let convertTOUTC= dueTicketDate &&  new Date(dueTicketDate);
          
          // const location =lookup(serviceAdd?.zip) || ""; // Getting timezone for the service address zip code this code is commneted as Bussiness changed logic
          if(isValid(convertTOUTC)){
            response.data.openTicketDate = convertTOUTC;
            // const localDate = toZonedTime(convertTOUTC, location) // converting UTC to particular state time zone, this code is commneted as Bussiness changed logic
            const roundOffDate = getMinutes(convertTOUTC) > 0 ? addHours(convertTOUTC, 1) : convertTOUTC;
            response.data.outageDate = format(roundOffDate, "EEEE, MMMM d");
            response.data.outageTime = format(roundOffDate, "h a")
          }
        }
        if(response?.data?.open?.length === 0 && response.data?.recently_closed?.length > 0){
          response.data.closeOutage = true;
          DwLogutil({stepName: EC_DW_LOGGING_STEPS?.closed_outage_banner});
          outageBannerGTM("outage_banner_resolved",companyName);
        }
        if(response?.data?.open?.length === 0 && response.data?.recently_closed?.length === 0
          && ((response?.data?.result?.toLowerCase() === "no outage found.") || (response?.data?.result === null))
          ){
          response.data.noOutage = true;
        }

        return response.data;
      }
    } catch (error) {
      throw error;
    }
  }
);

const outageStatus = ["new","in progress","on hold","monitoring","spares","resolved","field"];
 const outageRestoreStatus =["closed","canceled"];

 // openoutage banner Date and Time calculatoin
function findHighestDueDate(openTickets: any) {
  try {
      if (openTickets?.length === 1) {
    return openTickets[0]?.due_display;
  } else {
    //get longest resolution date and time
    let ticketonDue = openTickets.filter((ticket:any) => outageStatus.includes(ticket?.status_label?.toLowerCase()))
    .reduce((latest: any, ticket: any) => {
      return new Date(ticket.due_display) > new Date(latest.due_display) ? ticket?.due_display : latest?.due_display;
    });

    return ticketonDue;
  }
    
  } catch (error) {
     throw error;
  }
}

// Closed Outage Date and Time Caluclation 
function isWithin24Hours(resolvedOutage: any) {
    const browserNow = new Date().getTime();
    const twentyFourHoursMs = 24 * 60 * 60 * 1000;

    return resolvedOutage.filter((ticket: any) => {
        const utcTime = new Date(ticket?.resolved).getTime();
        const timeDiff = Math.abs(browserNow - utcTime);
        return (outageRestoreStatus?.includes(ticket?.status_label?.toLowerCase()) && timeDiff <= twentyFourHoursMs);
    });
}



const outageInformationSlice = createSlice({
  name: "/outageInformation",
  initialState,
  reducers: {
    clearOutageInformation: (state) => {
      return initialState;
    },
     outageInformationSliceSetInitialState: () => initialState,
  },
  extraReducers: (builder) => {
    builder.addCase(getOutageInformation.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getOutageInformation.fulfilled, (state, action) => {
      state.outageInformation = action.payload;
      state.openOutage = action?.payload?.openOutage || false;
      state.closeOutage = action?.payload?.closeOutage || false;
      state.noOutage = action?.payload?.noOutage || false;
      state.hideclose = action?.payload?.hideclose || false;
      state.outageDate = action?.payload?.outageDate || "";
      state.outageTime = action?.payload?.outageTime || "";
      state.loading = false;
      state.error = null;
    });
    builder.addCase(getOutageInformation.rejected, (state, action) => { 
      state.outageInformation = null;
      state.loading = false;
      state.error = action.error;
    });
  },
});

export const { clearOutageInformation, outageInformationSliceSetInitialState } =
  outageInformationSlice.actions;
export default outageInformationSlice.reducer;


